/* ─────────────────────────────────────────────────────────────────────────────
 * LQViewer — visionneuse d'images plein écran, partagée (portail LiveQuest).
 *
 * Un seul composant pour « toucher une vignette → la voir en grand » :
 *   • zoom au pincement, au double-toucher / double-clic, à la molette, aux
 *     boutons − / + et aux touches + / − ; déplacement de l'image zoomée ;
 *   • plusieurs images : balayage gauche/droite, boutons ‹ ›, flèches du clavier,
 *     compteur « 2 / 5 » ; légende et crédit sous l'image ;
 *   • fermeture : ✕, Échap, bouton Retour du téléphone (history.pushState /
 *     popstate), balayage vers le bas (image non zoomée), clic hors de l'image
 *     à la souris ;
 *   • accessibilité : role=dialog + aria-modal, focus piégé puis rendu au
 *     déclencheur, libellés, annonce du compteur, alternatives à chaque geste
 *     (WCAG 2.5.1 : le pincement a ses boutons, le balayage ses flèches),
 *     prefers-reduced-motion respecté (feuille lq-viewer.css).
 *
 * API :  LQViewer.open({ items:[{ src, alt, caption, credit }], index, opener, label })
 *        credit = { auteur, licence, url } ou une simple chaîne.
 *        LQViewer.close()   LQViewer.isOpen()
 *
 * Aucune dépendance. Aucun style= injecté en balisage : la feuille dédiée
 * portal/css/lq-viewer.css porte tout le décor ; seul le CSSOM (transform,
 * propriété --lqv-dim) est piloté ici, ce que la CSP autorise.
 *
 * ⚠ Bouton Retour : la visionneuse pose UNE entrée d'historique. Son écouteur
 * `popstate` est posé en CAPTURE dès le chargement du script et appelle
 * stopImmediatePropagation sur « ses » retours : l'éditeur mobile
 * (editor-mobile.js) a sa propre sentinelle d'historique et aurait sinon
 * refermé la feuille d'étape en même temps que la visionneuse. Ce fichier doit
 * donc rester chargé AVANT editor-mobile.js.
 * ───────────────────────────────────────────────────────────────────────────── */
(function (global) {
  'use strict';
  var doc = global.document;
  if (!doc || global.LQViewer) return;

  var MIN = 1, MAX = 5, DOUBLE_TAP = 2.5, BTN_STEP = 1.5;
  var S = null;          // session ouverte (null = fermée)
  var skipPop = false;   // le prochain popstate est celui de NOTRE history.back()

  var ICONS = {
    close: '<path d="M6 6l12 12M18 6L6 18"/>',
    prev: '<path d="M15 5l-7 7 7 7"/>',
    next: '<path d="M9 5l7 7-7 7"/>',
    plus: '<path d="M12 5v14M5 12h14"/>',
    minus: '<path d="M5 12h14"/>'
  };

  function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
  function el(tag, cls, txt) {
    var n = doc.createElement(tag);
    if (cls) n.className = cls;
    if (txt != null) n.textContent = txt;
    return n;
  }
  function icon(name) {
    var s = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
    s.setAttribute('viewBox', '0 0 24 24');
    s.setAttribute('width', '22'); s.setAttribute('height', '22');
    s.setAttribute('fill', 'none'); s.setAttribute('stroke', 'currentColor');
    s.setAttribute('stroke-width', '2.2'); s.setAttribute('stroke-linecap', 'round'); s.setAttribute('stroke-linejoin', 'round');
    s.setAttribute('aria-hidden', 'true'); s.setAttribute('focusable', 'false');
    s.innerHTML = ICONS[name]; // gabarit statique, jamais une donnée
    return s;
  }
  function btn(cls, label, ic) {
    var b = el('button', 'lqv-btn ' + cls);
    b.type = 'button';
    b.setAttribute('aria-label', label);
    b.appendChild(icon(ic));
    return b;
  }
  function pctText(s) { return Math.round(s * 100) + ' %'; }

  /* Crédit : « Crédit : auteur · licence » + lien « Source » (https seulement). */
  function creditParts(c) {
    if (!c) return null;
    if (typeof c === 'string') return c.trim() ? { text: c.trim(), url: '' } : null;
    var a = String(c.auteur || '').trim(), l = String(c.licence || '').trim();
    var u = /^https:\/\//i.test(String(c.url || '').trim()) ? String(c.url).trim() : '';
    if (!a && !l && !u) return null;
    var t = a ? 'Crédit : ' + a + (l ? ' · ' + l : '') : (l ? 'Licence : ' + l : '');
    return { text: t, url: u };
  }

  function normalize(items) {
    var out = [];
    (items || []).forEach(function (it) {
      if (!it) return;
      if (typeof it === 'string') it = { src: it };
      if (!it.src) return;
      out.push({ src: String(it.src), alt: it.alt || '', caption: String(it.caption || '').trim(), credit: creditParts(it.credit) });
    });
    return out;
  }

  /* ─────────────────────────────── géométrie ─────────────────────────────── */
  function bounds() {
    var W = S.stage.clientWidth, H = S.stage.clientHeight;
    var nw = S.img.naturalWidth || W, nh = S.img.naturalHeight || H;
    var f = Math.min(W / nw, H / nh);
    var cw = nw * f * S.s, ch = nh * f * S.s;
    return { W: W, H: H, cw: cw, ch: ch, mx: Math.max(0, (cw - W) / 2), my: Math.max(0, (ch - H) / 2) };
  }
  function clampT() {
    var b = bounds();
    S.tx = clamp(S.tx, -b.mx, b.mx);
    S.ty = clamp(S.ty, -b.my, b.my);
  }
  function paint() {
    if (!S) return;
    S.img.style.transform = 'translate3d(' + S.tx + 'px,' + S.ty + 'px,0) scale(' + S.s + ')';
    var zoomed = S.s > 1.01;
    S.stage.classList.toggle('is-zoomed', zoomed);
    S.pct.textContent = pctText(S.s);
    S.zout.disabled = !zoomed;
    S.zin.disabled = S.s >= MAX - 0.001;
  }
  function center() {
    var r = S.stage.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }
  /* Zoom à l'échelle `ns`, le point (px, py) — relatif au centre de la scène — restant sous le doigt. */
  function zoomAt(ns, px, py) {
    ns = clamp(ns, MIN, MAX);
    if (ns < 1.04) ns = 1;
    var r = ns / S.s;
    S.tx = px - (px - S.tx) * r;
    S.ty = py - (py - S.ty) * r;
    S.s = ns;
    clampT();
    paint();
  }
  function animate(on) { S.img.classList.toggle('is-anim', !!on); }
  function zoomBy(f) { animate(true); zoomAt(S.s * f, 0, 0); }
  function resetZoom() { S.s = 1; S.tx = 0; S.ty = 0; paint(); }
  /* Toucher / clic dans le cadre de l'image elle-même (pas dans les marges noires) ? */
  function onImage(x, y) {
    var c = center(), b = bounds();
    var cx = c.x + S.tx, cy = c.y + S.ty;
    return Math.abs(x - cx) <= b.cw / 2 && Math.abs(y - cy) <= b.ch / 2;
  }

  /* ─────────────────────────────── affichage ─────────────────────────────── */
  function show(i, dir) {
    var n = S.items.length, it = S.items[i];
    S.i = i;
    S.img.classList.remove('is-anim');
    resetZoom();
    S.slide.classList.remove('is-anim', 'in-next', 'in-prev');
    S.slide.style.transform = '';
    S.err.hidden = true;
    S.img.alt = it.alt || it.caption || ('Image ' + (i + 1) + ' sur ' + n);
    var token = ++S.tok;
    var ready = function () {
      if (!S || token !== S.tok) return;
      S.stage.classList.remove('is-loading');
      S.img.classList.remove('is-error');
      paint();
    };
    S.stage.classList.add('is-loading');
    S.img.onload = ready;
    S.img.onerror = function () {
      if (!S || token !== S.tok) return;
      S.stage.classList.remove('is-loading');
      S.img.classList.add('is-error');
      S.err.hidden = false;
    };
    if (S.img.getAttribute('src') === it.src && S.img.complete && S.img.naturalWidth) ready();
    else S.img.setAttribute('src', it.src);

    if (dir) {
      void S.slide.offsetWidth; // relance l'animation d'entrée
      S.slide.classList.add(dir > 0 ? 'in-next' : 'in-prev');
    }

    // Légende + crédit
    S.cap.textContent = it.caption;
    S.cap.hidden = !it.caption;
    S.cred.textContent = '';
    if (it.credit) {
      S.cred.appendChild(doc.createTextNode(it.credit.text));
      if (it.credit.url) {
        if (it.credit.text) S.cred.appendChild(doc.createTextNode(' · '));
        var a = el('a', 'lqv-src', 'Source');
        a.href = it.credit.url; a.target = '_blank'; a.rel = 'noopener noreferrer';
        a.appendChild(el('span', 'lqv-sr', ' (s’ouvre dans un nouvel onglet)'));
        S.cred.appendChild(a);
      }
    }
    S.cred.hidden = !(it.credit && (it.credit.text || it.credit.url));
    S.info.hidden = S.cap.hidden && S.cred.hidden;

    if (n > 1) {
      S.countVis.textContent = (i + 1) + ' / ' + n;
      S.countSr.textContent = 'Image ' + (i + 1) + ' sur ' + n;
      S.prev.disabled = i === 0;
      S.next.disabled = i === n - 1;
    }
    // Préchargement des voisines : le balayage suivant est instantané.
    [i - 1, i + 1].forEach(function (k) { if (S.items[k]) { var p = new global.Image(); p.src = S.items[k].src; } });
  }
  function go(d) {
    if (!S) return false;
    var j = S.i + d;
    if (j < 0 || j >= S.items.length) return false;
    show(j, d);
    return true;
  }

  /* ─────────────────────────────── gestes ─────────────────────────────── */
  function dist(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }
  function ptrs() { return Object.keys(S.ptr).map(function (k) { return S.ptr[k]; }); }

  function onDown(e) {
    if (!S || (e.pointerType === 'mouse' && e.button !== 0)) return;
    try { S.stage.setPointerCapture(e.pointerId); } catch (er) { /* capture indisponible */ }
    S.ptr[e.pointerId] = { x: e.clientX, y: e.clientY };
    animate(false);
    S.slide.classList.remove('is-anim');
    var p = ptrs();
    if (p.length === 2) {
      var c = center();
      var mx = (p[0].x + p[1].x) / 2 - c.x, my = (p[0].y + p[1].y) / 2 - c.y;
      S.pinch = { d: dist(p[0], p[1]) || 1, s: S.s, ux: (mx - S.tx) / S.s, uy: (my - S.ty) / S.s };
      S.mode = 'pinch';
      S.slide.style.transform = '';
    } else if (p.length === 1) {
      S.down = { x: e.clientX, y: e.clientY, t: e.timeStamp, tx: S.tx, ty: S.ty, type: e.pointerType };
      S.moved = false;
      S.mode = S.s > 1.01 ? 'pan' : 'pending';
    }
  }
  function onMove(e) {
    if (!S || !S.ptr[e.pointerId]) return;
    S.ptr[e.pointerId] = { x: e.clientX, y: e.clientY };
    var p = ptrs();
    if (S.mode === 'pinch' && p.length >= 2) {
      var c = center();
      var mx = (p[0].x + p[1].x) / 2 - c.x, my = (p[0].y + p[1].y) / 2 - c.y;
      var ns = clamp(S.pinch.s * dist(p[0], p[1]) / S.pinch.d, MIN, MAX);
      S.s = ns;
      S.tx = mx - S.pinch.ux * ns;
      S.ty = my - S.pinch.uy * ns;
      clampT(); paint(); S.moved = true;
      return;
    }
    if (p.length !== 1 || !S.down) return;
    var dx = e.clientX - S.down.x, dy = e.clientY - S.down.y;
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) S.moved = true;
    var touch = S.down.type !== 'mouse';
    if (S.mode === 'pending') {
      if (!touch) return;                                // souris, image entière : rien à glisser
      if (Math.abs(dx) < 9 && Math.abs(dy) < 9) return;
      if (Math.abs(dx) > Math.abs(dy)) S.mode = S.items.length > 1 ? 'swipe' : 'none';
      else S.mode = dy > 0 ? 'dismiss' : 'none';
    }
    if (S.mode === 'pan') {
      S.tx = S.down.tx + dx; S.ty = S.down.ty + dy;
      clampT(); paint();
    } else if (S.mode === 'swipe') {
      var edge = (dx > 0 && S.i === 0) || (dx < 0 && S.i === S.items.length - 1);
      S.slide.style.transform = 'translate3d(' + (edge ? dx * 0.3 : dx) + 'px,0,0)';
    } else if (S.mode === 'dismiss') {
      S.slide.style.transform = 'translate3d(0,' + dy + 'px,0)';
      S.root.style.setProperty('--lqv-dim', String(clamp(1 - dy / 420, 0.25, 1)));
    }
  }
  function onUp(e) {
    if (!S || !S.ptr[e.pointerId]) return;
    delete S.ptr[e.pointerId];
    try { S.stage.releasePointerCapture(e.pointerId); } catch (er) { /* déjà relâché */ }
    var left = ptrs();
    if (S.mode === 'pinch') {
      if (left.length === 1 && S.s > 1.01) {            // un doigt reste : on reprend le déplacement sans saut
        S.down = { x: left[0].x, y: left[0].y, t: e.timeStamp, tx: S.tx, ty: S.ty, type: 'touch' };
        S.mode = 'pan';
      } else if (!left.length) S.mode = null;
      else S.mode = 'none';
      if (S.s <= 1.04) { animate(true); zoomAt(1, 0, 0); }
      return;
    }
    if (left.length || !S.down) return;
    var dx = e.clientX - S.down.x, dy = e.clientY - S.down.y, dt = Math.max(1, e.timeStamp - S.down.t);
    var mode = S.mode; S.mode = null;
    if (e.type === 'pointercancel') { settleSlide(); return; }
    if (mode === 'swipe') {
      var W = S.stage.clientWidth, v = dx / dt;
      var next = dx < 0;
      if ((Math.abs(dx) > W * 0.2 || Math.abs(v) > 0.45) && go(next ? 1 : -1)) return;
      settleSlide();
    } else if (mode === 'dismiss') {
      if (dy > 110 || dy / dt > 0.6) close(false); else settleSlide();
    } else if (mode === 'none') {
      settleSlide();
    } else if (!S.moved) {                               // un simple toucher / clic
      var now = e.timeStamp, lt = S.lastTap;
      if (lt && now - lt.t < 320 && Math.abs(e.clientX - lt.x) < 32 && Math.abs(e.clientY - lt.y) < 32) {
        S.lastTap = null;
        var c = center();
        animate(true);
        if (S.s > 1.2) zoomAt(1, 0, 0); else zoomAt(DOUBLE_TAP, e.clientX - c.x, e.clientY - c.y);
      } else {
        S.lastTap = { t: now, x: e.clientX, y: e.clientY };
        // Souris : un clic dans la marge noire, image entière, referme (comme les autres visionneuses du portail).
        if (S.down.type === 'mouse' && S.s <= 1.01 && !onImage(e.clientX, e.clientY)) close(false);
      }
    }
  }
  function settleSlide() {
    S.slide.classList.add('is-anim');
    S.slide.style.transform = '';
    S.root.style.setProperty('--lqv-dim', '1');
  }
  function onWheel(e) {
    if (!S) return;
    e.preventDefault();
    var d = e.deltaY * (e.deltaMode === 1 ? 16 : (e.deltaMode === 2 ? 400 : 1));
    var c = center();
    animate(false);
    zoomAt(S.s * Math.exp(-d * (e.ctrlKey ? 0.012 : 0.0025)), e.clientX - c.x, e.clientY - c.y);
  }

  /* ─────────────────────────────── clavier / focus ─────────────────────────────── */
  function focusables() {
    return Array.prototype.filter.call(S.root.querySelectorAll('button, a[href]'), function (n) { return !n.disabled && !n.hidden && n.offsetParent !== null; });
  }
  function onKey(e) {
    if (!S) return;
    var k = e.key, used = true;
    if (k === 'Escape' || k === 'Esc') close(false);
    else if (k === 'Tab') {
      var f = focusables();
      if (!f.length) { S.close.focus(); }
      else {
        var i = f.indexOf(doc.activeElement);
        var j = e.shiftKey ? (i <= 0 ? f.length - 1 : i - 1) : (i === f.length - 1 ? 0 : i + 1);
        f[j].focus();
      }
    }
    else if (k === '+' || k === '=') zoomBy(BTN_STEP);
    else if (k === '-' || k === '_') zoomBy(1 / BTN_STEP);
    else if (k === '0') { animate(true); zoomAt(1, 0, 0); }
    else if (S.s > 1.01 && (k === 'ArrowUp' || k === 'ArrowDown' || (e.shiftKey && (k === 'ArrowLeft' || k === 'ArrowRight')))) {
      animate(true);
      S.tx += k === 'ArrowLeft' ? 80 : (k === 'ArrowRight' ? -80 : 0);
      S.ty += k === 'ArrowUp' ? 80 : (k === 'ArrowDown' ? -80 : 0);
      clampT(); paint();
    }
    else if (k === 'ArrowLeft') go(-1);
    else if (k === 'ArrowRight') go(1);
    else used = false;
    if (used) { e.preventDefault(); e.stopPropagation(); }
  }
  function onFocusIn(e) {
    if (S && !S.root.contains(e.target)) { var f = focusables(); (f[0] || S.close).focus(); }
  }
  function onResize() { if (S) { clampT(); paint(); } }

  /* ─────────────────────────────── historique ─────────────────────────────── */
  function onPop(e) {
    if (S) {                                    // « Retour » : on referme, et personne d'autre ne réagit
      e.stopImmediatePropagation();
      S.pushed = false;
      close(true);
    } else if (skipPop) {                       // l'écho de NOTRE history.back() (fermeture par ✕ / Échap)
      skipPop = false;
      e.stopImmediatePropagation();
    }
  }
  global.addEventListener('popstate', onPop, true);

  /* ─────────────────────────────── cycle de vie ─────────────────────────────── */
  function open(opts) {
    opts = opts || {};
    var items = normalize(opts.items);
    if (!items.length) return false;
    if (S) close(true);

    var root = el('div', 'lqv');
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', opts.label || 'Visionneuse d’images');
    root.setAttribute('aria-describedby', 'lqv-help');

    var bar = el('div', 'lqv-bar');
    var count = el('div', 'lqv-count');
    count.setAttribute('aria-live', 'polite'); count.setAttribute('aria-atomic', 'true');
    var countVis = el('span', 'lqv-count-vis'); countVis.setAttribute('aria-hidden', 'true');
    var countSr = el('span', 'lqv-sr');
    count.appendChild(countVis); count.appendChild(countSr);
    var tools = el('div', 'lqv-tools');
    tools.setAttribute('role', 'group'); tools.setAttribute('aria-label', 'Zoom');
    var zout = btn('lqv-zout', 'Zoom arrière', 'minus');
    var pct = el('output', 'lqv-pct', '100 %'); pct.setAttribute('aria-live', 'off');
    var zin = btn('lqv-zin', 'Zoom avant', 'plus');
    tools.appendChild(zout); tools.appendChild(pct); tools.appendChild(zin);
    var close_ = btn('lqv-close', 'Fermer la visionneuse', 'close');
    bar.appendChild(count); bar.appendChild(tools); bar.appendChild(close_);

    var mid = el('div', 'lqv-mid');
    var stage = el('div', 'lqv-stage is-loading');
    var slide = el('figure', 'lqv-slide');
    var img = el('img', 'lqv-img');
    img.draggable = false; img.decoding = 'async';
    var spin = el('div', 'lqv-spin'); spin.setAttribute('role', 'status'); spin.appendChild(el('span', 'lqv-sr', 'Chargement de l’image'));
    var err = el('p', 'lqv-err', 'Cette image ne s’affiche pas.'); err.setAttribute('role', 'status'); err.hidden = true;
    slide.appendChild(img); stage.appendChild(slide); stage.appendChild(spin); stage.appendChild(err);
    var prev = btn('lqv-nav lqv-prev', 'Image précédente', 'prev');
    var next = btn('lqv-nav lqv-next', 'Image suivante', 'next');
    mid.appendChild(stage); mid.appendChild(prev); mid.appendChild(next);

    var info = el('div', 'lqv-info');
    var cap = el('p', 'lqv-cap'), cred = el('p', 'lqv-credit');
    info.appendChild(cap); info.appendChild(cred);

    var help = el('p', 'lqv-sr', 'Flèches gauche et droite : image précédente ou suivante. Plus et moins : zoom. Échap : fermer.');
    help.id = 'lqv-help';

    root.appendChild(bar); root.appendChild(mid); root.appendChild(info); root.appendChild(help);

    var opener = opts.opener || doc.activeElement;
    S = {
      root: root, stage: stage, slide: slide, img: img, err: err, info: info, cap: cap, cred: cred,
      count: count, countVis: countVis, countSr: countSr, pct: pct, zout: zout, zin: zin, prev: prev, next: next, close: close_,
      items: items, i: 0, s: 1, tx: 0, ty: 0, ptr: {}, tok: 0, mode: null, down: null, lastTap: null, moved: false, pinch: null,
      opener: opener, openerKey: opener && opener.getAttribute ? opener.getAttribute('data-lqv-key') : null, pushed: false
    };
    if (items.length < 2) { count.hidden = true; prev.hidden = true; next.hidden = true; }

    close_.addEventListener('click', function () { close(false); });
    zout.addEventListener('click', function () { zoomBy(1 / BTN_STEP); });
    zin.addEventListener('click', function () { zoomBy(BTN_STEP); });
    prev.addEventListener('click', function () { go(-1); });
    next.addEventListener('click', function () { go(1); });
    stage.addEventListener('pointerdown', onDown);
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerup', onUp);
    stage.addEventListener('pointercancel', onUp);
    stage.addEventListener('wheel', onWheel, { passive: false });
    stage.addEventListener('dragstart', function (e) { e.preventDefault(); });
    img.addEventListener('transitionend', function () { img.classList.remove('is-anim'); });

    doc.body.appendChild(root);
    try {
      global.history.pushState(Object.assign({}, global.history.state, { lqv: 1 }), '', global.location.href);
      S.pushed = true;
    } catch (e) { /* historique indisponible : Échap et ✕ restent là */ }
    global.addEventListener('keydown', onKey, true);
    doc.addEventListener('focusin', onFocusIn, true);
    global.addEventListener('resize', onResize);
    show(clamp(opts.index | 0, 0, items.length - 1), 0);
    try { close_.focus({ preventScroll: true }); } catch (e) { close_.focus(); }
    return true;
  }

  function close(fromPop) {
    if (!S) return;
    var s = S; S = null;
    global.removeEventListener('keydown', onKey, true);
    doc.removeEventListener('focusin', onFocusIn, true);
    global.removeEventListener('resize', onResize);
    if (s.root.parentNode) s.root.parentNode.removeChild(s.root);
    if (s.pushed && fromPop !== true && global.history.state && global.history.state.lqv) {
      skipPop = true;
      try { global.history.back(); } catch (e) { skipPop = false; }
      setTimeout(function () { skipPop = false; }, 700);
    }
    // Focus rendu au déclencheur ; s'il a été redessiné entre-temps (liste rendue à nouveau), on le retrouve par sa clé.
    var t = s.opener;
    if ((!t || !doc.contains(t)) && s.openerKey) t = doc.querySelector('[data-lqv-key="' + s.openerKey.replace(/"/g, '') + '"]');
    if (t && t.focus) { try { t.focus({ preventScroll: true }); } catch (e) { t.focus(); } }
  }

  global.LQViewer = { open: open, close: function () { close(false); }, isOpen: function () { return !!S; } };
})(window);
